import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

export default function ForcePasswordChangeModal() {
  const { user, refreshUser, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (!user?.must_change_password) return null;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 8) {
      setErr("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      setErr("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated");
      await refreshUser();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={() => {}}>
      <DialogContent
        className="bg-[#121212] border-zinc-800 text-white sm:max-w-md"
        data-testid="force-password-change-modal"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md border border-orange-500/30 bg-orange-500/10 flex items-center justify-center">
              <ShieldAlert className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <DialogTitle className="text-white text-base">Set a new password</DialogTitle>
              <DialogDescription className="text-zinc-400 text-xs mt-1">
                This is a demo account — please rotate the password before continuing.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="label-uppercase">Current password</Label>
            <Input
              type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
              required data-testid="current-password-input"
              className="mt-1.5 bg-[#0A0A0A] border-zinc-800 focus-visible:ring-zinc-400"
            />
          </div>
          <div>
            <Label className="label-uppercase">New password</Label>
            <Input
              type="password" value={next} onChange={(e) => setNext(e.target.value)}
              required minLength={8} data-testid="new-password-input"
              className="mt-1.5 bg-[#0A0A0A] border-zinc-800 focus-visible:ring-zinc-400"
            />
          </div>
          <div>
            <Label className="label-uppercase">Confirm new password</Label>
            <Input
              type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              required minLength={8} data-testid="confirm-password-input"
              className="mt-1.5 bg-[#0A0A0A] border-zinc-800 focus-visible:ring-zinc-400"
            />
          </div>
          {err && <div className="text-xs text-red-400 border border-red-500/20 bg-red-500/5 rounded-md px-3 py-2" data-testid="password-error">{err}</div>}
          <div className="flex gap-2 pt-2">
            <Button
              type="submit" disabled={busy} data-testid="change-password-submit"
              className="flex-1 bg-white text-black hover:bg-zinc-200"
            >
              {busy ? "Updating…" : "Update password"}
            </Button>
            <Button
              type="button" variant="outline" onClick={logout} data-testid="cancel-password-change"
              className="border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Sign out
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
